import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, CreditCard, Lock } from "lucide-react";
import api from "@/lib/api";
import { useCart } from "@/contexts/CartContext";

export default function Checkout() {
  const { items } = useCart();
  const navigate = useNavigate();
  const [calc, setCalc] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    street: "",
    city: "",
    postal_code: "",
    country: "CH",
  });

  useEffect(() => {
    if (items.length === 0) {
      navigate("/cart");
      return;
    }
    api
      .post("/checkout/calculate", {
        items: items.map((i) => ({
          product_id: i.product_id,
          size: i.size,
          quantity: i.quantity,
        })),
      })
      .then((r) => setCalc(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Erreur de calcul"));
  }, [items, navigate]);

  const handleChange = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const res = await api.post("/checkout/create-session", {
        items: items.map((i) => ({
          product_id: i.product_id,
          size: i.size,
          quantity: i.quantity,
        })),
        address: form,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.url;
    } catch (err) {
      setError(err.response?.data?.detail || "Impossible de créer la session");
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-5 lg:px-8 py-10 lg:py-16">
      <Link
        to="/cart"
        className="inline-flex items-center gap-2 text-white/50 hover:text-magenta text-sm mb-6"
      >
        <ArrowLeft size={16} /> Retour au panier
      </Link>
      <h1 className="font-display text-4xl md:text-5xl mb-10">Commande</h1>

      <form
        onSubmit={submit}
        className="grid lg:grid-cols-3 gap-10"
        data-testid="checkout-form"
      >
        <div className="lg:col-span-2 space-y-6">
          <div className="fluo-card p-6">
            <h2 className="font-display text-2xl mb-4">Adresse de livraison</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <input
                required
                placeholder="Nom complet"
                className="fluo-input md:col-span-2"
                value={form.full_name}
                onChange={handleChange("full_name")}
                data-testid="input-full-name"
              />
              <input
                required
                type="email"
                placeholder="Email"
                className="fluo-input"
                value={form.email}
                onChange={handleChange("email")}
                data-testid="input-email"
              />
              <input
                required
                placeholder="Téléphone"
                className="fluo-input"
                value={form.phone}
                onChange={handleChange("phone")}
                data-testid="input-phone"
              />
              <input
                required
                placeholder="Rue et numéro"
                className="fluo-input md:col-span-2"
                value={form.street}
                onChange={handleChange("street")}
                data-testid="input-street"
              />
              <input
                required
                placeholder="NPA"
                className="fluo-input"
                value={form.postal_code}
                onChange={handleChange("postal_code")}
                data-testid="input-postal"
              />
              <input
                required
                placeholder="Ville"
                className="fluo-input"
                value={form.city}
                onChange={handleChange("city")}
                data-testid="input-city"
              />
              <select
                className="fluo-input md:col-span-2"
                value={form.country}
                onChange={handleChange("country")}
                data-testid="input-country"
              >
                <option value="CH">Suisse</option>
                <option value="FR">France</option>
                <option value="DE">Allemagne</option>
                <option value="IT">Italie</option>
              </select>
            </div>
          </div>

          {error && (
            <div className="border border-magenta/50 p-4 text-magenta" data-testid="checkout-error">
              {error}
            </div>
          )}
        </div>

        <aside className="fluo-card p-6 h-fit lg:sticky lg:top-24">
          <h2 className="font-display text-2xl mb-4">Récapitulatif</h2>
          {calc ? (
            <>
              <ul className="text-sm space-y-2 mb-4 max-h-56 overflow-auto">
                {calc.items.map((i) => (
                  <li
                    key={`${i.product_id}-${i.size}`}
                    className="flex justify-between text-white/70"
                  >
                    <span>
                      {i.product_name}{" "}
                      <span className="text-white/40">
                        ({i.size}) × {i.quantity}
                      </span>
                    </span>
                    <span>{i.subtotal.toFixed(2)}</span>
                  </li>
                ))}
              </ul>
              <div className="space-y-1 text-sm border-t border-white/10 pt-3">
                <div className="flex justify-between">
                  <span className="text-white/60">Sous-total</span>
                  <span>{calc.subtotal.toFixed(2)} CHF</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/60">{calc.shipping_label}</span>
                  <span>{calc.shipping_cost.toFixed(2)} CHF</span>
                </div>
              </div>
              <div className="border-t border-white/10 mt-4 pt-4 flex justify-between font-display text-xl">
                <span>Total</span>
                <span className="text-magenta">{calc.total.toFixed(2)} CHF</span>
              </div>
            </>
          ) : (
            <div className="text-white/40">Calcul…</div>
          )}

          <button
            type="submit"
            disabled={submitting || !calc}
            className="btn-fluo w-full mt-6"
            data-testid="pay-btn"
          >
            <CreditCard size={18} />
            {submitting ? "Redirection…" : "Payer maintenant"}
          </button>
          <div className="flex items-center justify-center gap-2 text-xs text-white/40 mt-3">
            <Lock size={12} /> Paiement sécurisé Stripe
          </div>
        </aside>
      </form>
    </div>
  );
}
